/**
 * Text Style Fields - Reusable templates for text/button styling
 *
 * Extracted from headerSectionConfig.ts for reuse across sections.
 * Based on LabelBlock schema from @segmently/schema-models.
 */

import type { FieldConfig } from '../../types/editorFieldTypes';
import {
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS,
  FONT_WEIGHT_OPTIONS_SHORT,
  TEXT_ALIGN_OPTIONS,
  NUMBER_0_50_OPTIONS,
  NUMBER_0_100_OPTIONS,
  BOOLEAN_OPTIONS,
  BUTTON_VISIBILITY_OPTIONS,
  OPACITY_OPTIONS,
  SCALE_MODE_OPTIONS,
  ICON_PLACEMENT_OPTIONS,
} from './options';

/**
 * Common text style fields for buttons (non-nullable)
 *
 * Order matches screenshot layout (2 columns, left to right, top to bottom):
 * Row 1: button-background | font family
 * Row 2: line height | font size
 * Row 3: font weight | text color
 * Row 4: background color | line through color
 *
 * isNullable: false for required typography; line-through is nullable (presence == strikethrough on)
 */
export const BUTTON_TEXT_STYLE_FIELDS: FieldConfig[] = [
  // Row 1
  {
    path: '.styles.backgroundColor',
    label: 'text background color',
    type: 'color',
    defaultValue: '#ffffff',
    isNullable: true,
  },
  {
    path: '.styles.fontFamily',
    label: 'font family',
    type: 'select',
    options: [...FONT_FAMILY_OPTIONS],
    defaultValue: 'SF Pro',
    isNullable: false,
  },
  // Row 2
  {
    path: '.styles.lineHeight',
    label: 'line height',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 18,
    defaultScrollValue: 18,
    isNullable: false,
  },
  {
    path: '.styles.fontSize',
    label: 'font size',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 15,
    defaultScrollValue: 18,
    isNullable: false,
  },
  // Row 3
  {
    path: '.styles.fontWeight',
    label: 'font weight',
    type: 'select',
    options: [...FONT_WEIGHT_OPTIONS],
    defaultValue: 400,
    isNullable: false,
    numericValue: true,
  },
  {
    path: '.styles.color',
    label: 'text color',
    type: 'color',
    defaultValue: '#ffffff',
    isNullable: false,
  },
  // Row 4
  {
    path: '.styles.textAlign',
    label: 'text align',
    type: 'select',
    options: [...TEXT_ALIGN_OPTIONS],
    defaultValue: 'center',
    isNullable: false,
  },
  // Row 5
  {
    path: '.styles.textDecorationColor',
    label: 'line through color',
    type: 'color',
    defaultValue: '#000000',
    isNullable: true,
  },
  // Row 6: background opacity | background corner radius (V2 TypographyStyle only)
  {
    path: '.styles.backgroundColorOpacity',
    label: 'bg opacity',
    type: 'numberCombo',
    options: OPACITY_OPTIONS,
    defaultValue: 1,
    isNullable: true,
  },
  {
    path: '.styles.backgroundCornerRadius',
    label: 'bg corner radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 0,
    defaultScrollValue: 4,
    isNullable: true,
  },
  // Note: Padding fields (paddingLeft, paddingRight, paddingTop, paddingBottom)
  // are rendered via PaddingElement cross-layout component, not as individual fields
];

/**
 * Title style fields (nullable) for use in DashesWithTitle, titles etc.
 *
 * Similar to BUTTON_TEXT_STYLE_FIELDS but with isNullable: true
 * and includes textAlign field.
 */
export const TITLE_TEXT_STYLE_FIELDS: FieldConfig[] = [
  // Row 1
  {
    path: '.styles.backgroundColor',
    label: 'background color',
    type: 'color',
    defaultValue: '#ffffff',
    isNullable: true,
  },
  {
    path: '.styles.fontFamily',
    label: 'font family',
    type: 'select',
    options: [...FONT_FAMILY_OPTIONS],
    defaultValue: 'SF Pro',
    isNullable: true,
  },
  // Row 2
  {
    path: '.styles.lineHeight',
    label: 'line height',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 22.5,
    defaultScrollValue: 18,
    isNullable: true,
  },
  {
    path: '.styles.fontSize',
    label: 'font size',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 15,
    defaultScrollValue: 18,
    isNullable: true,
  },
  // Row 3
  {
    path: '.styles.fontWeight',
    label: 'font weight',
    type: 'select',
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    defaultValue: 400,
    isNullable: true,
    numericValue: true,
  },
  {
    path: '.styles.color',
    label: 'text color',
    type: 'color',
    defaultValue: '#000000',
    isNullable: true,
  },
  // Row 4
  {
    path: '.styles.textAlign',
    label: 'text align',
    type: 'select',
    options: [...TEXT_ALIGN_OPTIONS],
    defaultValue: 'center',
    isNullable: true,
  },
  {
    path: '.styles.textDecorationColor',
    label: 'line through color',
    type: 'color',
    defaultValue: '#000000',
    isNullable: true,
  },
  // Note: Box padding fields (paddingLeft, paddingRight, paddingTop, paddingBottom)
  // are rendered via PaddingElement cross-layout component, not as individual fields
];

/**
 * V2 TypographyStyle title fields.
 *
 * V1 LabelBlock does not support backgroundColorOpacity/backgroundCornerRadius.
 * Keep these fields out of TITLE_TEXT_STYLE_FIELDS and use this template only in
 * V2 configs that write to LocalizedLabel.appearance.
 */
export const V2_TITLE_TEXT_STYLE_FIELDS: FieldConfig[] = [
  ...TITLE_TEXT_STYLE_FIELDS,
  {
    path: '.styles.backgroundColorOpacity',
    label: 'bg opacity',
    type: 'numberCombo',
    options: OPACITY_OPTIONS,
    defaultValue: 1,
    isNullable: true,
  },
  {
    path: '.styles.backgroundCornerRadius',
    label: 'bg corner radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 0,
    defaultScrollValue: 4,
    isNullable: true,
  },
];

/**
 * Placeholder text style fields (without backgroundColor)
 *
 * Used for input field placeholder text where the field container
 * already has its own backgroundColor setting (field.styles.backgroundColor).
 * Excludes backgroundColor to avoid duplicate controls in the editor.
 */
export const PLACEHOLDER_TEXT_STYLE_FIELDS: FieldConfig[] = [
  // Row 1 (no backgroundColor - handled by field.styles.backgroundColor)
  {
    path: '.styles.fontFamily',
    label: 'font family',
    type: 'select',
    options: [...FONT_FAMILY_OPTIONS],
    defaultValue: 'SF Pro',
    isNullable: true,
  },
  // Row 2
  {
    path: '.styles.lineHeight',
    label: 'line height',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 22.5,
    defaultScrollValue: 18,
    isNullable: true,
  },
  {
    path: '.styles.fontSize',
    label: 'font size',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 15,
    defaultScrollValue: 18,
    isNullable: true,
  },
  // Row 3
  {
    path: '.styles.fontWeight',
    label: 'font weight',
    type: 'select',
    options: [...FONT_WEIGHT_OPTIONS_SHORT],
    defaultValue: 400,
    isNullable: true,
    numericValue: true,
  },
  // Note: text color removed - handled by field.styles.enteredTextColor in Field Styles
  {
    path: '.styles.textAlign',
    label: 'text align',
    type: 'select',
    options: [...TEXT_ALIGN_OPTIONS],
    defaultValue: 'center',
    isNullable: true,
  },
  {
    path: '.styles.textDecorationColor',
    label: 'line through color',
    type: 'color',
    defaultValue: '#000000',
    isNullable: true,
  },
];

/**
 * Create style fields for a button/text element with full path prefix
 *
 * @param basePath - Base path to prepend to style paths (e.g., 'navigationBar.back.content')
 * @param template - Style fields template to use (default: BUTTON_TEXT_STYLE_FIELDS)
 * @returns Array of FieldConfig with full paths
 *
 * @example
 * createTextStyleFields('navigationBar.back.content')
 * // Returns fields with paths like 'navigationBar.back.content.styles.fontSize'
 */
export function createTextStyleFields(
  basePath: string,
  template: FieldConfig[] = BUTTON_TEXT_STYLE_FIELDS
): FieldConfig[] {
  return template.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * Alias for backward compatibility
 * @deprecated Use createTextStyleFields instead
 */
export const createButtonStyleFields = createTextStyleFields;

/**
 * Create style fields for V2 TypographyStyle (LocalizedLabel.appearance).
 *
 * V2 schema uses `.appearance.*` instead of `.styles.*` for TypographyStyle,
 * and the alignment field is `align` (not `textAlign`).
 *
 * @param basePath - Base path (e.g., 'content.textField.placeholder')
 * @returns Fields with paths like 'content.textField.placeholder.appearance.color'
 */
export function createV2AppearanceStyleFields(basePath: string): FieldConfig[] {
  return BUTTON_TEXT_STYLE_FIELDS.map((field) => {
    let subPath = field.path.replace('.styles.', '.appearance.');
    // V2 TypographyStyle uses 'align' not 'textAlign'
    if (subPath.endsWith('.textAlign')) {
      subPath = subPath.replace('.textAlign', '.align');
    }
    return { ...field, path: `${basePath}${subPath}` };
  });
}

// ============================================
// Button Container Style Fields (ButtonBlock)
// ============================================

/**
 * Button container style fields (ButtonBlock from schema)
 *
 * Properties: visibility, backgroundColor, padding*, borderRadius, borderColor, borderWidth, width, height
 * Used for styling the button box itself, separate from text content styles.
 *
 * Order matches screenshot layout (2 columns, left to right, top to bottom):
 * Row 0: visibility (behavior setting - shown first)
 * Row 1: full-width | button-height
 * Row 2: button-background | button-border-width
 * Row 3: button-border-color | button-border-radius
 */
export const BUTTON_CONTAINER_STYLE_FIELDS: FieldConfig[] = [
  // Row 0: Visibility (behavior setting)
  {
    path: '.styles.visibility',
    label: 'visibility',
    type: 'select',
    options: [...BUTTON_VISIBILITY_OPTIONS],
    defaultValue: 'default',
    isNullable: true,
  },
  // Row 1: Full Width | Height
  {
    path: '.styles.fullWidth',
    label: 'full-width',
    type: 'select',
    options: [...BOOLEAN_OPTIONS],
    defaultValue: 'false',
    isNullable: true,
  },
  {
    path: '.styles.height',
    label: 'button-height',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 50,
    defaultScrollValue: 50,
    isNullable: true,
  },
  // Row 2: Background | Border Width
  {
    path: '.styles.backgroundColor',
    label: 'button-background',
    type: 'color',
    defaultValue: '#007AFF',
    isNullable: true,
  },
  {
    path: '.styles.borderWidth',
    label: 'button-border-width',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 0,
    defaultScrollValue: 1,
    isNullable: true,
  },
  // Row 3: Border Color | Border Radius
  {
    path: '.styles.borderColor',
    label: 'button-border-color',
    type: 'color',
    defaultValue: '#007AFF',
    isNullable: true,
  },
  {
    path: '.styles.borderRadius',
    label: 'button-border-radius',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 8,
    defaultScrollValue: 8,
    isNullable: true,
  },
  // Row 4: Width (optional, not always visible)
  {
    path: '.styles.width',
    label: 'width',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: undefined,
    isNullable: true,
  },
];

// ============================================
// V2-Only Button Appearance Fields (TapTargetStyle)
// ============================================

/**
 * V2-only button appearance fields: gradient, shadow, opacity, animation.
 * These properties exist on TapTargetStyle (V2) but NOT on V1 ButtonBlock.
 * Separated from BUTTON_CONTAINER_STYLE_FIELDS to avoid leaking into V1 editors.
 */
export const V2_BUTTON_APPEARANCE_FIELDS: FieldConfig[] = [
  // Row 1: Gradient start | Gradient end
  {
    path: '.styles.gradient.colors.0',
    label: 'gradient-start',
    type: 'color',
    defaultValue: undefined,
    isNullable: true,
  },
  {
    path: '.styles.gradient.colors.1',
    label: 'gradient-end',
    type: 'color',
    defaultValue: undefined,
    isNullable: true,
  },
  // Row 2: Shadow color | Shadow blur
  {
    path: '.styles.shadow.color',
    label: 'shadow-color',
    type: 'color',
    defaultValue: '#000000',
    isNullable: true,
  },
  {
    path: '.styles.shadow.blur',
    label: 'shadow-blur',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 4,
    defaultScrollValue: 4,
    isNullable: true,
  },
  // Row 3: Shadow offsetY | Opacity
  {
    path: '.styles.shadow.offsetY',
    label: 'shadow-offset-y',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 2,
    defaultScrollValue: 2,
    isNullable: true,
  },
  {
    path: '.styles.opacity',
    label: 'opacity',
    type: 'slider',
    defaultValue: 1,
    isNullable: true,
  },
  // Row 4: Animation preset
  {
    path: '.styles.animation.kind',
    label: 'animation',
    type: 'select',
    options: [
      { value: '', label: 'None' },
      { value: 'pulse', label: 'Pulse' },
      { value: 'shimmer', label: 'Shimmer' },
      { value: 'bounce', label: 'Bounce' },
      { value: 'shake', label: 'Shake' },
      { value: 'glow', label: 'Glow' },
      { value: 'ripple', label: 'Ripple' },
      { value: 'scale', label: 'Scale' },
      { value: 'fadeIn', label: 'Fade In' },
      { value: 'slideUp', label: 'Slide Up' },
      { value: 'wiggle', label: 'Wiggle' },
    ],
    defaultValue: '',
    isNullable: true,
  },
  // Row 5: v2.17 enter/tap/idle animation presets. Extraction-only fields —
  // EffectsEditor renders its own selects; runtime gives these priority over
  // the legacy `animation` above.
  {
    path: '.styles.enterAnimation.preset',
    label: 'enterAnimation',
    type: 'select',
    options: [],
    defaultValue: '',
    isNullable: true,
  },
  {
    path: '.styles.tapAnimation.preset',
    label: 'tapAnimation',
    type: 'select',
    options: [],
    defaultValue: '',
    isNullable: true,
  },
  {
    path: '.styles.idleAnimation.preset',
    label: 'idleAnimation',
    type: 'select',
    options: [],
    defaultValue: '',
    isNullable: true,
  },
];

/**
 * Create button container style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'footer.button1')
 * @returns Array of FieldConfig with full paths like 'footer.button1.styles.backgroundColor'
 */
export function createButtonContainerStyleFields(basePath: string): FieldConfig[] {
  return BUTTON_CONTAINER_STYLE_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * Create V2-only button appearance fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'content.actionBar.primary')
 * @returns Array of FieldConfig with full paths like 'content.actionBar.primary.styles.gradient.colors.0'
 */
export function createV2ButtonAppearanceFields(basePath: string): FieldConfig[] {
  return V2_BUTTON_APPEARANCE_FIELDS.map((field) => ({
    ...field,
    path: `${basePath}${field.path}`,
  }));
}

/**
 * Icon fields for a V2 TapTarget button (icon + iconPlacement).
 * Reusable across all V2 button configs.
 *
 * @param basePath - Base path of the TapTarget (e.g., 'content.actionBar.primary')
 */
export function createV2IconFields(basePath: string): FieldConfig[] {
  return [
    {
      path: `${basePath}.icon`,
      label: 'icon',
      type: 'imageUpload',
      defaultValue: undefined,
      isNullable: true,
    },
    {
      path: `${basePath}.iconPlacement`,
      label: 'icon-placement',
      type: 'select',
      options: [...ICON_PLACEMENT_OPTIONS],
      defaultValue: 'start',
      isNullable: true,
    },
    // Icon appearance fields (ImageStyle on LocalizedImage)
    {
      path: `${basePath}.icon.appearance.scaleMode`,
      label: 'scale mode',
      type: 'select',
      options: [...SCALE_MODE_OPTIONS],
      defaultValue: 'scaleAspectFit',
      isNullable: true,
    },
    {
      path: `${basePath}.icon.appearance.dimensions.width`,
      label: 'width',
      type: 'numberCombo',
      options: NUMBER_0_100_OPTIONS,
      defaultScrollValue: 24,
      isNullable: true,
    },
    {
      path: `${basePath}.icon.appearance.dimensions.height`,
      label: 'height',
      type: 'numberCombo',
      options: NUMBER_0_100_OPTIONS,
      defaultScrollValue: 24,
      isNullable: true,
    },
    {
      path: `${basePath}.icon.appearance.cornerRadii.uniform`,
      label: 'corner radius',
      type: 'numberCombo',
      options: NUMBER_0_50_OPTIONS,
      defaultScrollValue: 0,
      isNullable: true,
    },
  ];
}

// ============================================
// Footer Container Style Fields (BasicFooterBlock)
// ============================================

/**
 * Footer container style fields (BasicFooterBlock from schema)
 *
 * Properties: leftButtonWidthPercentage, paddingLeft, paddingRight, paddingTop, paddingBottom
 */
export const FOOTER_CONTAINER_STYLE_FIELDS: FieldConfig[] = [
  {
    path: '.leftButtonWidthPercentage',
    label: 'left button width %',
    type: 'slider',
    min: 0,
    max: 100,
    step: 5,
    defaultValue: 50,
    isNullable: true,
  },
  {
    path: '.paddingLeft',
    label: 'padding left',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 16,
    defaultScrollValue: 16,
    isNullable: true,
  },
  {
    path: '.paddingRight',
    label: 'padding right',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 16,
    defaultScrollValue: 16,
    isNullable: true,
  },
  {
    path: '.paddingTop',
    label: 'padding top',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 16,
    defaultScrollValue: 16,
    isNullable: true,
  },
  {
    path: '.paddingBottom',
    label: 'padding bottom',
    type: 'numberCombo',
    options: NUMBER_0_50_OPTIONS,
    defaultValue: 16,
    defaultScrollValue: 16,
    isNullable: true,
  },
];

/**
 * Create footer container style fields with full path prefix
 *
 * @param basePath - Base path to prepend (e.g., 'footer.styles')
 * @returns Array of FieldConfig with full paths like 'footer.styles.paddingLeft'
 */
export function createFooterContainerStyleFields(basePath: string): FieldConfig[] {
  return FOOTER_CONTAINER_STYLE_FIELDS.map((field) => ({
    ...field,
    // Note: path already starts with '.', so basePath ends without dot
    path: `${basePath}${field.path}`,
  }));
}
